import React, { useState, useCallback, useMemo } from 'react'
import { Select, Label, SearchField } from '@sainsburys-tech/luna-form'
import { AsyncAutocompleteField } from '@sainsburys-tech/luna-autocomplete'
import { SearchButton, Button } from '@sainsburys-tech/luna-button'
import {
  ProgressIndicator,
  ProgressSpinner,
  ProgressMessage
} from '@sainsburys-tech/luna-progress'
import { Display1, Body1 } from '@sainsburys-tech/luna-typography'
import { GridWrapper, GridItem, Container } from '@sainsburys-tech/luna-grid'
import { Modal, Table } from '@sainsburys-tech/luna-react'
import {
  ToggleButtonGroup,
  ToggleButton
} from '@jsainsburyplc/jsluna-toggle-button'
import { Card } from '@sainsburys-tech/luna-card'
import './SearchIncidents.scss'

import {
  SearchType,
  ISearchOption,
  ISearchState,
  IIncidentDetails,
  UNPAID,
  CIVIL_RECOVERY
} from '../../types'
import useIncidents from '../../hooks/useIncidents'
import ErrorMessage from '../ErrorMessage'
import Loading from '../Loading'
import storesApi from '../../api/storesApi'
import incidentsApi from '../../api/incidentsApi'
import { useContext } from 'react'
import { AppContext } from '../../context/AppContext'
import { Pagination } from '@sainsburys-tech/luna-pagination'
import { IPagination } from '../../types/Pagination/IPagination'
import usePage from '../../hooks/usePage'

const SearchIncidents: React.FC = () => {
  const [searchResource, searchLoader] = useIncidents()
  const context = useContext(AppContext)
  const pageType = 'IncidentsPage'
  const { page, handlePage } = usePage(pageType)
  const [searchState, setSearchState] = useState<ISearchState>({
    isSearching: false,
    hasSearched: false,
    searchType: SearchType.VEHICLE_REGISTRATION,
    searchString: '',
    showNoResults: false,
    searchError: ''
  })
  const [showIncidentDetailsModal, setShowIncidentDetailsModal] =
    useState(false)
  const [selectedIncident, setSelectedIncident] =
    useState<IIncidentDetails | null>(null)
  const [selectedStoreOption, setSelectedStoreOption] = useState<{
    label: string
    value: string
  } | null>(null)
  const [mobileFilterValue, setMobileFilterValue] = useState<string>(UNPAID)
  const [isClosingIncident, setIsClosingIncident] = useState<boolean>(false)
  const [closureError, setClosureError] = useState<string | null>(null)

  const isWithinAllowedTimeframe = useCallback((startFuelingTime: Date) => {
    const currentDate = new Date()
    const fuelingDate = new Date(startFuelingTime)
    const cutoffDate = new Date('2025-10-28T00:00:00.000Z')
    
    const daysBack = fuelingDate < cutoffDate ? 7 : 2
    
    const earliestAllowedDate = new Date(currentDate)
    earliestAllowedDate.setDate(currentDate.getDate() - daysBack)
    earliestAllowedDate.setHours(0, 0, 0, 0)
    
    const todayEnd = new Date(currentDate)
    todayEnd.setHours(23, 59, 59, 999)
    
    return fuelingDate >= earliestAllowedDate && fuelingDate <= todayEnd
  }, [])

  const canTakePayment = useCallback((incident: IIncidentDetails) => {
    if (incident.incidentStatus === 'Closed') return false

    if (incident.incidentStatus === 'AwaitingSurvey') return false

    if (incident.civilRecoveryData?.rejectedReason === 'Less than £10.00' || incident.totalFuelValue < 10) {
      return true
    }

    return isWithinAllowedTimeframe(incident.startFuelingTime)
  }, [isWithinAllowedTimeframe])

  const searchOptions: ISearchOption[] = useMemo(
    () => [
      { label: 'Vehicle Registration', value: SearchType.VEHICLE_REGISTRATION },
      { label: 'Store where fuel was dispensed', value: SearchType.STORE_NAME }
    ],
    []
  )
  
  const civilRecoveryCount = useMemo(() => {
    if (!searchResource.data?.results) return 0
    const count = searchResource.data.results.filter(
      (x: IIncidentDetails) => 
        (x.incidentStatus !== 'Closed' && !canTakePayment(x))
    ).length
    return count
  }, [searchResource.data?.results, canTakePayment])

  const unpaidCount = useMemo(() => {
    if (!searchResource.data?.results) return 0
    const count = searchResource.data.results.filter(
      (x: IIncidentDetails) => 
        (canTakePayment(x)) || 
        x.incidentStatus === 'Closed'
    ).length
    return count
  }, [searchResource.data?.results, canTakePayment])

  const filterMobileResults = useCallback(() => {
    if (!searchResource.data?.results) return []

    if (mobileFilterValue === UNPAID) {
      return searchResource.data.results.filter(
        (x: IIncidentDetails) =>
          (canTakePayment(x)) || 
          x.incidentStatus === 'Closed'
      )
    }

    return searchResource.data.results.filter(
      (x: IIncidentDetails) =>
        (x.incidentStatus !== 'Closed' && !canTakePayment(x))
    )
  }, [searchResource.data?.results, mobileFilterValue, canTakePayment])
  
  const handleMobileFilterToggle = (event: any, value: string) => {
    setMobileFilterValue(value ?? event.target.value)
  }

  const mobileToggleOptions = useMemo(() => {
    const options = [
      {
        label: `Unpaid/Paid (${unpaidCount})`,
        value: UNPAID
      },
      {
        label: `Gone to Civil Recovery (${civilRecoveryCount})`,
        value: CIVIL_RECOVERY
      }
    ]
    return options
  }, [unpaidCount, civilRecoveryCount])

  const handleSearchTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value
      setSearchState(prev => ({
        ...prev,
        searchType: value as SearchType,
        searchString: '',
        hasSearched: false,
        showNoResults: false,
        searchError: ''
      }))
      setSelectedStoreOption(null)
    },
    []
  )

  const handleSearchStringChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setSearchState(prev => ({
        ...prev,
        searchString: value,
        showNoResults: false,
        searchError: ''
      }))
    },
    []
  )

  const handleSearch = useCallback(async () => {
    if (!searchState.searchString.trim()) {
      setSearchState(prev => ({
        ...prev,
        searchError: 'Please enter a search term'
      }))
      return
    }

    setSearchState(prev => ({
      ...prev,
      isSearching: true,
      searchError: ''
    }))

    setMobileFilterValue(UNPAID)

    try {
      await searchLoader.current(
        searchState.searchType,
        searchState.searchString.trim(),
        page
      )
      setSearchState(prev => ({
        ...prev,
        isSearching: false,
        hasSearched: true
      }))
    } catch (error) {
      setSearchState(prev => ({
        ...prev,
        isSearching: false,
        searchError: 'Search failed. Please try again.'
      }))
    }
  }, [searchState.searchType, searchState.searchString, searchLoader, page])

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleSearch()
      }
    },
    [handleSearch]
  )

  const handlePageChange = useCallback(
    async (newPage: number) => {
      const pageNumber = newPage - 1
      handlePage(pageType, pageNumber)
      
      if (!searchState.searchString.trim()) return

      setSearchState(prev => ({
        ...prev,
        isSearching: true,
        searchError: ''
      }))

      try {
        await searchLoader.current(
          searchState.searchType,
          searchState.searchString.trim(),
          pageNumber
        )
        setSearchState(prev => ({
          ...prev,
          isSearching: false,
          hasSearched: true
        }))
      } catch (error) {
        setSearchState(prev => ({
          ...prev,
          isSearching: false,
          searchError: 'Search failed. Please try again.'
        }))
      }
    },
    [handlePage, pageType, searchState.searchType, searchState.searchString, searchLoader]
  )

  const openIncidentDetailsModal = useCallback((incident: IIncidentDetails) => {
    setSelectedIncident(incident)
    setShowIncidentDetailsModal(true)
    setClosureError(null)
  }, [])

  const closeIncidentDetailsModal = useCallback(() => {
    setShowIncidentDetailsModal(false)
    setSelectedIncident(null)
  }, [])

  const handlePaymentTaken = useCallback(async () => {
    if (selectedIncident && context?.apiClient && !isClosingIncident) {
      setIsClosingIncident(true)
      setClosureError(null)

      try {
        await incidentsApi.closeIncident(context.apiClient, {
          incidentId: selectedIncident.incidentId
        })

        if (searchLoader.current) {
          await searchLoader.current(
            searchState.searchType,
            searchState.searchString.trim(),
            page
          )
        }

        closeIncidentDetailsModal()
      } catch (error) {
        console.error('Failed to close incident:', error)
        setClosureError('Failed to close incident. Please try again.')
      } finally {
        setIsClosingIncident(false)
      }
    }
  }, [
    selectedIncident,
    context,
    searchLoader,
    searchState.searchType,
    searchState.searchString,
    closeIncidentDetailsModal,
    isClosingIncident
  ])

  const handleStoreSelect = useCallback(
    async (option: { label: string; value: string } | null) => {
      setSelectedStoreOption(option)
      setSearchState(prev => ({
        ...prev,
        searchString: option ? option.value : '',
        showNoResults: false,
        searchError: ''
      }))

      if (option && option.value.trim()) {
        setSearchState(prev => ({
          ...prev,
          isSearching: true,
          searchError: ''
        }))

        setMobileFilterValue(UNPAID)

        try {
          await searchLoader.current(SearchType.STORE_NAME, option.value.trim(), page)
          setSearchState(prev => ({
            ...prev,
            isSearching: false,
            hasSearched: true
          }))
        } catch (error) {
          setSearchState(prev => ({
            ...prev,
            isSearching: false,
            searchError: 'Search failed. Please try again.'
          }))
        }
      }
    },
    [searchLoader, page]
  )
  const pagination = useMemo<IPagination | undefined>(() => {
    if (searchResource.hasLoaded && !searchResource.hasError) {
      const searchResponse = searchResource.data
      return searchResponse?.metadata?.pagination
    }
    return undefined
  }, [searchResource])
  const getPlaceholder = useCallback(() => {
    switch (searchState.searchType) {
      case SearchType.STORE_NAME:
        return 'e.g. Coventry'
      case SearchType.VEHICLE_REGISTRATION:
        return 'e.g. AB12 CDE'
      default:
        return ''
    }
  }, [searchState.searchType])

  const IncidentSearchCard = ({ incident }: { incident: IIncidentDetails }) => {
    const handleCardClick = () => {
      if (canTakePayment(incident)) {
        openIncidentDetailsModal(incident)
      }
    }

    return (
      <GridItem size={{ xs: '1/1' }}>
        <Card
          className="ln-u-margin-bottom c-incident-search-card"
          soft
          onClick={handleCardClick}
        >
          <div className="ln-u-padding*2 c-incident-search-card__content">
            <div className="c-incident-search-card__info">
              <Container>
                  <Body1 className="ln-u-font-weight-bold ln-u-margin-bottom c-incident-search-card__name">
                    {incident.licencePlate || 'Unknown Vehicle Registration'} - {getStatusText(incident)}
                  </Body1>
              </Container>
              <Body1 className="ln-u-color-subdued c-incident-search-card__address">
                {new Date(incident.startFuelingTime).toLocaleDateString('en-GB', { 
                  day: '2-digit', 
                  month: '2-digit',
                  year: 'numeric'
                })} {new Date(incident.startFuelingTime).toISOString().slice(11, 16)} - {incident.storeName || 'Unknown Store'}
              </Body1>
            </div>
            <div className="c-incident-search-card__arrow">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 4L10 8L6 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </Card>
      </GridItem>
    )
  }

  const getStatusText = (incident: IIncidentDetails) => {
    if (incident.incidentStatus === 'Closed') return 'Closed/Paid'
    if (incident.incidentStatus === 'AwaitingSurvey') return 'Awaiting Survey'
    if (!canTakePayment(incident)) {
      return 'Sent to Civil Recovery'
    }

    return 'Take payment'
  }

  const IncidentTable = ({ incidents }: { incidents: IIncidentDetails[] }) => {

    const getStatusBadgeClass = (incident: IIncidentDetails) => {
      const status = getStatusText(incident)
      const baseClass = 'c-results-table__action-badge'
      const isClickable = canTakePayment(incident)

      switch (status) {
        case 'Sent to Civil Recovery':
          return `${baseClass} ${baseClass}--red ${baseClass}--disabled`
        case 'Take payment':
          return `${baseClass} ${baseClass}--green ${isClickable ? '' : `${baseClass}--disabled`}`
        case 'Closed/Paid':
          return `${baseClass} ${baseClass}--grey ${baseClass}--disabled`
        case 'Awaiting Survey':
          return `${baseClass} ${baseClass}--orange ${baseClass}--disabled`
        default:
          return `${baseClass} ${baseClass}--grey ${baseClass}--disabled`
      }
    }

    const handleActionClick = (
      incident: IIncidentDetails,
      event: React.MouseEvent
    ) => {
      event.stopPropagation()

      if (!canTakePayment(incident)) {
        return
      }

      openIncidentDetailsModal(incident)
    }

    return (
      <Table
        rowKey="incidentId"
        data={incidents}
        caption="Incident search results"
        visuallyHiddenCaption
        className="ln-u-margin-top*2"
        columns={[
          {
            name: 'Id',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.sequenceNumber || 'Unknown'
            })
          },
          {
            name: 'Date',
            accessor: (rowData: IIncidentDetails) => ({
              value: new Date(rowData.startFuelingTime).toLocaleDateString(
                'en-GB',
                {
                  day: '2-digit',
                  month: '2-digit', 
                  year: 'numeric'
                }
              ) + ' ' + new Date(rowData.startFuelingTime).toISOString().slice(11, 16)
            }),
            sort:
              (_accessor: any, ascending: boolean) =>
              (a: IIncidentDetails, b: IIncidentDetails) => {
                const dateA = new Date(a.startFuelingTime).getTime()
                const dateB = new Date(b.startFuelingTime).getTime()
                return ascending ? dateA - dateB : dateB - dateA
              }
          },
          {
            name: 'Location',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.storeName || 'Fuel Theft'
            })
          },
          {
            name: 'Type',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.incidentType || 'Unknown'
            })
          },
          {
            name: 'Reason',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.driveOffType
            })
          },
          {
            name: 'Registration',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.licencePlate || 'Unknown'
            })
          },
          {
            name: 'Vehicle Model',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.vehicleModel || 'Unknown'
            })
          },
          {
            name: 'Vehicle Color',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.vehicleColour || 'Unknown'
            })
          },
          {
            name: 'Fuel Type',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.fuelType || 'Unknown'
            })
          },
          {
            name: 'Fuel Value',
            accessor: (rowData: IIncidentDetails) => ({
              value: rowData.fuelValue?.toFixed(2) || '0.00'
            }),
            render: ({ value }: { value: string }) => <span>£{value}</span>,
            sort:
              (_accessor: any, ascending: boolean) =>
              (a: IIncidentDetails, b: IIncidentDetails) => {
                const valueA = parseFloat(a.fuelValue?.toString() || '0')
                const valueB = parseFloat(b.fuelValue?.toString() || '0')
                return ascending ? valueA - valueB : valueB - valueA
              }
          },
          {
            name: 'Action',
            accessor: (rowData: IIncidentDetails) => ({
              value: getStatusText(rowData),
              incident: rowData
            }),
            render: ({
              value,
              incident
            }: {
              value: string
              incident: IIncidentDetails
            }) => {
              const isClickable = canTakePayment(incident)
              
              const getTooltipMessage = () => {
                if (incident.incidentStatus === 'Closed') {
                  return 'No action available - incident is closed'
                }
                if (!canTakePayment(incident)) {
                  return 'No action available - fueling time exceeded allowed timeframe (current day + 2 days)'
                }
                return 'Click to take action'
              }

              return (
                <span
                  className={getStatusBadgeClass(incident)}
                  onClick={
                    isClickable
                      ? e => handleActionClick(incident, e)
                      : undefined
                  }
                  title={getTooltipMessage()}
                >
                  {value}
                </span>
              )
            }
          }
        ]}
      />

    )
  }

  const renderSearchResults = () => {
    if (searchResource.hasError) {
      return (
        <Container
          size="xs"
          className="ln-u-text-align-center ln-u-margin-top*2"
        >
          <ErrorMessage errorMessage="Failed to search incidents" />
        </Container>
      )
    }

    if (!searchResource.data?.results || searchResource.data.results.length === 0) {
      return (
        <Container
          size="xs"
          className="ln-u-text-align-center ln-u-margin-top*2"
        >
          <ErrorMessage errorMessage="No Unpaid/Paid records found." />
        </Container>
      )
    }

    return (
      <Container size="xl" className="search-incidents">
        <Card>
          <div className="search-results__desktop">
            <IncidentTable incidents={searchResource.data.results} />
          </div>

          <div className="search-results__mobile">
            {searchResource.data.results.length > 0 && (
              <div className="ln-u-margin-bottom*2">
                <div className="ln-u-text-align-center">
                  <ToggleButtonGroup
                    value={mobileFilterValue}
                    onChange={handleMobileFilterToggle}
                    alpha={true}
                  >
                    {mobileToggleOptions.map(option => (
                      <ToggleButton key={option.value} value={option.value}>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </div>
              </div>
            )}
            <GridWrapper
              matrix
              gutterSize="sm"
              className="ln-u-margin-bottom*2"
            >
              {filterMobileResults().map(
                (incident: IIncidentDetails, index: number) => (
                  <IncidentSearchCard key={index} incident={incident} />
                )
              )}
            </GridWrapper>
          </div>
        </Card>
      </Container>
    )
  }

  return (
    <div>
      <Container size="xs">
        <GridWrapper>
          <GridItem size={{ xs: '1/1' }}>
            <Label htmlFor="search-type">Search for a record</Label>
          </GridItem>
          <GridItem size={{ xs: '1/1', md: '1/2' }}>
            <Select
              name="search-type"
              id="search-type"
              options={searchOptions}
              value={searchState.searchType}
              defaultValue={SearchType.VEHICLE_REGISTRATION}
              onChange={handleSearchTypeChange}
              className="ln-u-margin-bottom"
              placeholder=""
              emptyOption={false}
            />
          </GridItem>
          <GridItem size={{ xs: '1/1', md: '1/2' }}>
            {searchState.searchType === SearchType.STORE_NAME ? (
              <AsyncAutocompleteField
                name="search-string"
                id="search-string"
                placeholder={getPlaceholder()}
                value={selectedStoreOption?.value}
                onSelect={handleStoreSelect}
                minChars={3}
                onKeyPress={handleKeyPress}
                loadOptions={async (search: string) => {
                  if (!context || !search) return Promise.resolve([])
                  try {
                    const response = await storesApi.getStores(context, search)
                    const stores = Array.isArray(response)
                      ? response
                      : (response as { results?: any[] })?.results || []
                    return stores.map((store: any) => ({
                      label: store.name || store.code || 'Unknown Store',
                      value: store.district_code || store.code || ''
                    }))
                  } catch {
                    return []
                  }
                }}
                error={searchState.searchError}
                className="ln-u-margin-bottom"
                hasButton
                button={<SearchButton onClick={handleSearch} label="Search" />}
              />
            ) : (
              <SearchField
                name="search-string"
                id="search-string"
                placeholder={getPlaceholder()}
                value={searchState.searchString}
                onChange={handleSearchStringChange}
                onKeyPress={handleKeyPress}
                error={searchState.searchError}
                className="ln-u-margin-bottom"
                hasButton
                button={<SearchButton onClick={handleSearch} label="Search" />}
              />
            )}
          </GridItem>
        </GridWrapper>
      </Container>

      {searchState.isSearching && (
        <Container size="xs">
          <Loading size="large" />
        </Container>
      )}

      {searchState.hasSearched && !searchState.isSearching && (
        <div className="ln-u-margin-top*2">{renderSearchResults()}</div>
      )}

      {pagination && pagination.totalPages > 0 && (
        <Pagination
          id="s-pagination-bar"
          className="ln-u-text-align-center ln-u-margin-ends*3"
          showFirstAndLast
          current={pagination.currentPage + 1}
          total={pagination.totalPages}
          onChange={(page: number, e: any) => {
            e.preventDefault()
            handlePageChange(page)
          }}
        />
      )}
      

      {searchState.hasSearched &&
        !searchState.isSearching &&
        !searchResource.hasError && (
          <Container
            size="xs"
            className="ln-u-text-align-center ln-u-margin-top*4"
          >
            <Body1>Records that are over 12 months old are not shown.</Body1>
          </Container>
        )}

      <Modal
        open={showIncidentDetailsModal}
        handleClose={closeIncidentDetailsModal}
        size="md"
      >
        <Container className="ln-u-padding*3">
          <Display1 className="ln-u-margin-bottom*2">Incident Details</Display1>
          {selectedIncident && (
            <div className="c-incident-details">
              <div className="c-incident-details__section">
                <div className="c-incident-details__grid">
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">
                      Transaction Id
                    </Body1>
                    <Body1>{selectedIncident.transactionId}</Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">Date</Body1>
                    <Body1>
                                          {new Date(selectedIncident.startFuelingTime).toLocaleDateString(
                        'en-GB',
                        {
                          day: '2-digit',
                          month: '2-digit', 
                          year: 'numeric'
                        }
                      )} {new Date(selectedIncident.startFuelingTime).toISOString().slice(11, 16)}
                    </Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">Location</Body1>
                    <Body1>{selectedIncident.storeName}</Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">Type</Body1>
                    <Body1>
                      {selectedIncident.incidentType || 'Fuel Theft'}
                    </Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">Reason</Body1>
                    <Body1>{selectedIncident.driveOffType || 'N/A'}</Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">
                      Registration
                    </Body1>
                    <Body1>{selectedIncident.licencePlate || 'N/A'}</Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">
                      Vehicle Model
                    </Body1>
                    <Body1>{selectedIncident.vehicleModel || 'N/A'}</Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">
                      Vehicle Color
                    </Body1>
                    <Body1>{selectedIncident.vehicleColour || 'N/A'}</Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">Fuel Type</Body1>
                    <Body1>{selectedIncident.fuelType || 'Unknown'}</Body1>
                  </div>
                  <div className="c-incident-details__item">
                    <Body1 className="ln-u-font-weight-bold">Fuel Value</Body1>
                    <Body1>
                      £{selectedIncident.fuelValue?.toFixed(2) || '0.00'}
                    </Body1>
                  </div>
                </div>
              </div>

              {canTakePayment(selectedIncident) ? (
                <div className="c-incident-details__actions">
                  <Body1 className="ln-u-font-weight-bold">
                    Please make sure you have taken payment on the till before
                    continuing.
                  </Body1>
                </div>
              ) : null}

              {canTakePayment(selectedIncident) ? (
                <div className="c-incident-details__actions ln-u-margin-top*3 ln-u-text-align-center">
                  <Button
                    variant="filled"
                    color="brand"
                    onClick={handlePaymentTaken}
                    className="ln-u-width-full"
                    disabled={isClosingIncident}
                  >
                    {isClosingIncident ? (
                      <ProgressIndicator
                        loading
                        preventFocus
                        message={
                          <ProgressMessage>Processing...</ProgressMessage>
                        }
                      >
                        <ProgressSpinner
                          color="light"
                          size="small"
                          className="ln-u-margin-right"
                        />
                      </ProgressIndicator>
                    ) : (
                      'Payment Taken on Till'
                    )}
                  </Button>
                  {closureError && (
                    <div className="ln-u-margin-top ln-u-text-align-center">
                      <Body1 className="ln-u-font-weight-bold">
                        {closureError}
                      </Body1>
                    </div>
                  )}
                </div>
              ) : (
                <div className="c-incident-details__no-action">
                  <Body1 className="ln-u-font-weight-bold">
                    No action required -{' '}
                    {selectedIncident.incidentStatus === 'Closed'
                      ? 'already been paid'
                      : !canTakePayment(selectedIncident)
                      ? 'fueling time exceeded allowed timeframe, sent to Civil Recovery'
                      : 'with Civil Recovery'}
                    .
                  </Body1>
                </div>
              )}
            </div>
          )}
        </Container>
      </Modal>
    </div>
  )
}

export default SearchIncidents
